import { Card, Col, Stack } from "react-bootstrap";

import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Chart } from "react-chartjs-2";
import annotationPlugin from "chartjs-plugin-annotation";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useMemo } from "react";
import LoadingIndicator from "../LoadingIndicator";
import ErrorResponse from "../ErrorResponse";
import RATooltip from "../RATooltip";
import { toAutoDecimals } from "../HelperFunctions";
ChartJS.register(ArcElement, Tooltip, Legend, annotationPlugin, ChartDataLabels);

export default function RatingsCard({ data, allTimeData, loading, allTimeLoading, error, allTimeError }) {
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                top: 1,
            },
        },
        borderRadius: 5,
        plugins: {
            legend: {
                display: false,
                position: "bottom",
                labels: {
                    usePointStyle: true,
                },
            },
            tooltip: {
                enabled: false,
            },
            datalabels: {
                color: "#142533",
                font: {
                    size: 16,
                    weight: "bold",
                },
                formatter: function (value) {
                    return value.toLocaleString();
                },
            },
            annotation: {
                clip: false,
                annotations: {
                    avg: {
                        type: "line",
                        label: {
                            display: true,
                            content: `AVG ${toAutoDecimals(data?.average, 0).toLocaleString()}`,
                            position: "start",
                            color: "#142533",
                            backgroundColor: "transparent",
                            padding: 0,
                            yAdjust: 8,
                        },
                        yMin: data?.average ?? 0,
                        yMax: data?.average ?? 0,
                        borderColor: "#142533",
                        borderWidth: 3,
                    },
                },
            },
        },
        scales: {
            y: {
                max: Math.max(data?.peak, data?.average) * 1.05,
                ticks: {
                    display: false,
                },
                grid: {
                    display: false,
                },
                border: {
                    display: false,
                },
            },
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    display: false,
                },
            },
        },

        interaction: {
            mode: "index",
        },
    };

    const chartData = useMemo(() => {
        if (data) {
            return {
                labels: ["Time Frame"],
                datasets: [
                    {
                        type: "bar",
                        backgroundColor: ["#fcd76b"],
                        data: [Math.round(data.time_frame)],
                    },
                ],
            };
        }
    }, [data]);

    return (
        <Card>
            <Card.Header className="d-flex align-items-center px-2">
                <span className="offset-1 flex-grow-1">Ratings</span>
                <div className="col-auto d-flex justify-content-end">
                    <RATooltip size={20}>
                        <Stack gap={2}>
                            <span>Average is calculated on the past 13 weeks from the start date selected.</span>
                            <span>Ratings excludes corrupted days, whilst averages excludes both holidays and corrupted days.</span>
                        </Stack>
                    </RATooltip>
                </div>
            </Card.Header>
            <Card.Body className="card-health-chart d-flex flex-column pb-1">
                {loading ? (
                    <LoadingIndicator width={85} />
                ) : error ? (
                    <ErrorResponse errorCode={error} />
                ) : (
                    <>
                        <Col>
                            <Chart data={chartData} options={chartOptions} />
                        </Col>
                        <div>
                            <Stack gap={1} className="fs-8">
                                <Stack direction="horizontal" gap={1} className="align-items-center">
                                    <span className="text-muted">ALL TIME PEAK: </span>
                                    {allTimeLoading ? (
                                        <div>
                                            <LoadingIndicator width={15} showText={false} />
                                        </div>
                                    ) : (
                                        <span className="fw-bold">{allTimeData?.toLocaleString() ?? "-"}</span>
                                    )}
                                </Stack>
                                <Stack direction="horizontal" gap={1} className="align-items-center">
                                    <span className="text-muted">PEAK: </span>
                                    <span className="fw-bold">{data?.peak?.toLocaleString()}</span>
                                </Stack>
                                <Stack direction="horizontal" gap={1} className="align-items-center">
                                    <span className="text-muted">LOW: </span>
                                    <span className="fw-bold">{data?.low?.toLocaleString()}</span>
                                </Stack>
                            </Stack>
                        </div>
                    </>
                )}
            </Card.Body>
        </Card>
    );
}
